import { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import { Search, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Link } from 'react-router-dom';

const STATUS_LABELS = {
  pending: { label: 'بانتظار التأكيد', color: 'bg-amber-500/20 text-amber-400' },
  confirmed: { label: 'مؤكد', color: 'bg-green-500/20 text-green-400' },
  active: { label: 'جارٍ', color: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'مكتمل', color: 'bg-slate-500/20 text-slate-300' },
  cancelled: { label: 'ملغي', color: 'bg-red-500/20 text-red-400' },
};

export default function AdminBookings() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery(['admin-bookings', page, search, statusFilter], () => {
    const p = new URLSearchParams({ page, limit: 20 });
    if (search) p.set('search', search);
    if (statusFilter) p.set('status', statusFilter);
    return api.get(`/admin/bookings?${p}`).then(r => r.data);
  }, { keepPreviousData: true });

  const bookings = data?.bookings || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">إدارة الحجوزات</h1>
        <span className="text-slate-400 text-sm">{data?.total || 0} حجز</span>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="form-input w-full pr-9 text-sm py-2.5" placeholder="رقم الحجز، الإعلان، المستأجر..." />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="form-input text-sm py-2.5">
          <option value="">جميع الحالات</option>
          {Object.entries(STATUS_LABELS).map(([v, d]) => <option key={v} value={v}>{d.label}</option>)}
        </select>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/50 text-slate-400">
                <th className="text-right py-3 px-4 font-medium">رقم الحجز</th>
                <th className="text-right py-3 px-4 font-medium">الإعلان</th>
                <th className="text-right py-3 px-4 font-medium">المستأجر</th>
                <th className="text-right py-3 px-4 font-medium">تاريخ الحجز</th>
                <th className="text-right py-3 px-4 font-medium">المبلغ</th>
                <th className="text-right py-3 px-4 font-medium">العمولة</th>
                <th className="text-right py-3 px-4 font-medium">الحالة</th>
                <th className="text-right py-3 px-4 font-medium">التفاصيل</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="py-8 text-center text-slate-500">جارٍ التحميل...</td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-slate-500">لا توجد حجوزات</td></tr>
              ) : bookings.map(b => (
                <tr key={b.id} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                  <td className="py-3 px-4 font-mono text-xs text-slate-300">{b.booking_reference}</td>
                  <td className="py-3 px-4 text-white max-w-[140px] truncate">{b.listing_title}</td>
                  <td className="py-3 px-4 text-slate-400">{b.renter_name}</td>
                  <td className="py-3 px-4 text-slate-400 text-xs">
                    {format(new Date(b.start_time), 'dd/MM/yyyy HH:mm')}
                  </td>
                  <td className="py-3 px-4 text-primary-400 font-medium">{b.total_amount} {b.currency}</td>
                  <td className="py-3 px-4 text-green-400">{b.platform_commission} {b.currency}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_LABELS[b.status]?.color}`}>
                      {STATUS_LABELS[b.status]?.label}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <Link to={`/dashboard/bookings/${b.uuid}`} className="text-xs text-primary-400 hover:text-primary-300">عرض</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700/50">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn-secondary text-sm py-1.5 disabled:opacity-40">السابق</button>
            <span className="text-slate-400 text-sm">صفحة {page} من {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="btn-secondary text-sm py-1.5 disabled:opacity-40">التالي</button>
          </div>
        )}
      </div>
    </div>
  );
}
